import axios from 'axios';

// Use environment variable or fallback to localhost
let API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
console.log("API Base URL:", API_BASE_URL);

// Check the protocol to avoid issues
if (!API_BASE_URL.startsWith('http://') && !API_BASE_URL.startsWith('https://')) {
  console.warn("API Base URL doesn't include protocol, adding http://");
  API_BASE_URL = `http://${API_BASE_URL}`;
}

// Create an axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Upload a PDF file and create a new session
 * @param {File} file - The PDF file to upload
 * @param {Function} onProgress - Callback function for tracking upload progress
 * @returns {Promise<Object>} - Session data including session_id
 */
export const uploadPDF = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        if (onProgress) onProgress(percentCompleted);
      },
    });
    
    // Store the response for later use (includes pdf_path needed for analysis)
    if (response.data && response.data.session_id) {
      localStorage.setItem(`upload_${response.data.session_id}`, JSON.stringify(response.data));
      console.log('Stored upload response in localStorage for session:', response.data.session_id);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error uploading PDF:', error);
    throw error;
  }
};

/**
 * Send a chat message and get a response
 * @param {string} sessionId - The session ID
 * @param {string} message - The message to send
 * @param {number} pageNumber - The current page number (for context)
 * @param {boolean} fullDocumentAccess - Whether to give the assistant access to the full document
 * @returns {Promise<Object>} - Chat response data
 */
export const sendChatMessage = async (sessionId, message, pageNumber, fullDocumentAccess = true) => {
  try {
    // The backend expects the format defined in the ChatMessage model
    console.log(`Making API call to ${API_BASE_URL}/chat`);
    
    // Check if it's a direct chat session (starts with 'direct-')
    const isDirectSession = sessionId.startsWith('direct-');
    
    // Just in case there's a very long document summary request, add a timeout
    const timeoutMs = message.toLowerCase().includes('summary') ? 60000 : 30000;
    
    // For direct sessions, we make a call to a different endpoint or modify the payload
    if (isDirectSession) {
      try {
        const response = await api.post('/direct-chat', {
          session_id: sessionId,
          message: message
        }, {
          timeout: timeoutMs
        });
        
        return response.data;
      } catch (error) {
        console.error('Error with direct chat API call, falling back to simulation:', error);
        // If backend doesn't support direct-chat endpoint yet, we'll use simulation
        // This is a graceful fallback until the backend is updated
        throw error; // This will trigger the simulation in the ChatInterface component
      }
    } else {
      // Normal document-based chat
      const response = await api.post('/chat', {
        session_id: sessionId,
        message: message,
        page_number: pageNumber,
        full_document_access: fullDocumentAccess
      }, {
        timeout: timeoutMs
      });
      
      return response.data;
    }
  } catch (error) {
    console.error('Error sending chat message:', error);
    
    // Only for document summary requests, provide fallback if backend fails
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('summary') && 
        (lowerMessage.includes('document') || lowerMessage.includes('pdf'))) {
      console.log('Backend failed on document summary request, using fallback');
      return {
        message: "I tried to analyze your document, but encountered an issue processing the full summary. Could you try asking about specific aspects or sections of the document instead?",
        session_id: sessionId
      };
    }
    
    throw error;
  }
};

/**
 * Run analysis with selected agents
 * @param {string} sessionId - The session ID
 * @param {number} pageNumber - The page number to analyze
 * @param {Array<string>} agents - Array of agent names to run
 * @returns {Promise<Object>} - Analysis results
 */
export const runAnalysis = async (sessionId, pageNumber, agents) => {
  try {
    console.log(`Running analysis for page ${pageNumber} with agents: ${agents.join(', ')}`);
    
    // Map frontend agent names to backend agent names
    const agentMap = {
      'SFEE': 'sfee',
      'EOF': 'eof',
      'Galenica': 'galenica',
      'EFEX': 'efex',
      'Grammar': 'grammar',
      'Citation': 'citation'
    };
    
    // Convert agent names to backend format
    const mappedAgents = agents.map(agent => agentMap[agent] || agent.toLowerCase());
    
    // Get PDF path from session storage (this should be set during upload)
    const uploadResponse = JSON.parse(localStorage.getItem(`upload_${sessionId}`)) || {};
    const pdfPath = uploadResponse.pdf_path;
    
    if (!pdfPath) {
      throw new Error('PDF path not found in session storage. Please try uploading the document again.');
    }
    
    // Call the analyze endpoint
    const response = await api.post('/analyze', {
      pdf_path: pdfPath,
      page_number: pageNumber,
      agents: mappedAgents,
    });
    
    console.log('Analysis response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error running analysis:', error);
    throw error;
  }
};

/**
 * Get text content for a specific page
 * @param {string} sessionId - The session ID
 * @param {number} pageNumber - The page number
 * @returns {Promise<Object>} - Page text data
 */
export const getPageText = async (sessionId, pageNumber) => {
  try {
    const response = await api.get('/page-text', {
      params: {
        session_id: sessionId,
        page_number: pageNumber,
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error getting page text:', error);
    throw error;
  }
};

// Create API service object
const apiService = {
  uploadPDF,
  sendChatMessage,
  runAnalysis,
  getPageText,
};

export default apiService;